"use client";

import React from "react";
import { Building2, Clock, Zap, Truck, ArrowRight, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
export default function DeliveryPlacementSoCal() {
  const considerations = [{
    icon: Building2,
    title: "Site Access Review",
    description: "Placement area, gates, clearances, surface conditions, and other site-access considerations are reviewed for the requested trailer."
  }, {
    icon: Clock,
    title: "Delivery Coordination",
    description: "Requested dates, delivery location, intended use, preferred trailer size, and placement details are reviewed with each quote."
  }, {
    icon: Zap,
    title: "Power and Fuel Planning",
    description: "Available site power and trailer-specific power or fuel requirements are confirmed individually before the rental."
  }, {
    icon: Truck,
    title: "53-Foot Reefer Requirements",
    description: "Loading, delivery, placement, site access, power or fuel requirements, and suitability for the 53-foot reefer are reviewed individually."
  }];
  return <section id="deliveryplacementsocal" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-mono uppercase tracking-wider text-xs">
            Delivery & Site Access
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Delivery & Placement Across Southern California
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Site access, delivery details, and equipment requirements are reviewed individually with each quote in Southern California.
          </p>
        </div>

        {/* Notice Callout */}
        <div className="mt-8">
          <Alert className="border-primary/20 bg-accent/40 text-foreground">
            <MapPin className="text-primary size-5" />
            <AlertTitle className="text-base font-semibold text-foreground">
              Quote-by-Quote Review
            </AlertTitle>
            <AlertDescription className="text-sm text-foreground/80 mt-1 leading-relaxed">
              Delivery location, requested placement, site access, available power or fuel, and other requirements are reviewed with each quote.
            </AlertDescription>
          </Alert>
        </div>

        {/* Grid of Key Considerations */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {considerations.map((item, i) => {
          const Icon = item.icon;
          return <Card key={item.title} data-index={i} className="flex flex-col justify-between border-border bg-card shadow-xs hover:shadow-md transition-shadow duration-200">
                <div>
                  <CardHeader className="space-y-3 pb-3">
                    <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </div>
              </Card>;
        })}
        </div>

        {/* Pre-Booking Checklist & Quote Direct CTA */}
        <div className="mt-12 lg:mt-16 rounded-2xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-2xl space-y-2">
              <div className="flex items-center gap-2 text-primary font-medium text-sm">
                <Clock className="size-4" />
                <span>Need help planning your rental?</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight text-card-foreground">
                Request a Rental Quote
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Provide your requested dates, delivery location, intended use, temperature requirements, preferred trailer size, site-access details, and available power. Availability, requirements, and pricing are confirmed with each quote.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3.5 shrink-0">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/ContactRequestAQuotePage">
                  Request a Quote
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/RefrigeratedTrailersPage">
                  View Trailers
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}